import { REHYDRATE } from "redux-persist";
import type { OverwriteStoreAction } from "../../types/actionTypes";
import type { WrpfRecordCategory, WrpfRecordsState } from "../handoff/types";
import { isWrpfRecordCategory, WRPF_RECORD_CATEGORY_PRIORITY } from "./recordCategories";

type RehydrateAction = {
  type: typeof REHYDRATE;
  payload?: {
    wrpfRecords?: Partial<WrpfRecordsState>;
  };
};

export type RefreshWrpfRecordsAction = {
  type: "REFRESH_WRPF_RECORDS";
  records: WrpfRecordsState;
};

export type SetWrpfRecordCategoriesOverrideAction = {
  type: "SET_WRPF_RECORD_CATEGORIES_OVERRIDE";
  categories: ReadonlyArray<WrpfRecordCategory> | null;
};

type Action =
  | OverwriteStoreAction
  | RehydrateAction
  | RefreshWrpfRecordsAction
  | SetWrpfRecordCategoriesOverrideAction
  | { type: string };

const sortedCategories = (categories: ReadonlyArray<unknown>): WrpfRecordCategory[] =>
  WRPF_RECORD_CATEGORY_PRIORITY.filter((category) => categories.some((candidate) => candidate === category));

const sameCategories = (a: ReadonlyArray<WrpfRecordCategory>, b: ReadonlyArray<WrpfRecordCategory>): boolean => {
  const left = sortedCategories(a);
  const right = sortedCategories(b);
  return left.length === right.length && left.every((category, index) => category === right[index]);
};

/** Una elección igual a la de la web no es un cambio: vuelve a seguir a la web. */
const normalizeOverride = (
  override: unknown,
  webCategories: ReadonlyArray<WrpfRecordCategory>,
): ReadonlyArray<WrpfRecordCategory> | null => {
  if (!Array.isArray(override)) return null;
  const categories = sortedCategories(override.filter(isWrpfRecordCategory));
  return sameCategories(categories, webCategories) ? null : categories;
};

const normalizeWrpfRecordsState = (incoming?: Partial<WrpfRecordsState>): WrpfRecordsState => {
  const recordCategories = incoming?.recordCategories ?? [];
  return {
    tournamentScope: incoming?.tournamentScope ?? null,
    recordCategories,
    recordCategoriesOverride: normalizeOverride(incoming?.recordCategoriesOverride, recordCategories),
    currentRecords: incoming?.currentRecords ?? [],
  };
};

export const defaultWrpfRecordsState = (): WrpfRecordsState => normalizeWrpfRecordsState();

/** Categorías con las que se detectan récords: la elección del operador o, si no hay, las de la web. */
export const selectEnabledRecordCategories = (records: WrpfRecordsState): ReadonlyArray<WrpfRecordCategory> =>
  records.recordCategoriesOverride ?? records.recordCategories ?? [];

export const refreshWrpfRecords = (records: WrpfRecordsState): RefreshWrpfRecordsAction => ({
  type: "REFRESH_WRPF_RECORDS",
  records,
});

/** `null` descarta la elección local y vuelve a lo configurado en la web. */
export const setWrpfRecordCategoriesOverride = (
  categories: ReadonlyArray<WrpfRecordCategory> | null,
): SetWrpfRecordCategoriesOverrideAction => ({
  type: "SET_WRPF_RECORD_CATEGORIES_OVERRIDE",
  categories,
});

export default function wrpfRecordsReducer(
  state: WrpfRecordsState = defaultWrpfRecordsState(),
  action: Action,
): WrpfRecordsState {
  if (action.type === REHYDRATE) {
    const rehydrate = action as RehydrateAction;
    return normalizeWrpfRecordsState(rehydrate.payload?.wrpfRecords ?? state);
  }
  if (action.type === "OVERWRITE_STORE") {
    const incoming = (action as OverwriteStoreAction).store.wrpfRecords;
    return normalizeWrpfRecordsState(incoming);
  }
  if (action.type === "REFRESH_WRPF_RECORDS") {
    // Reabrir el handoff trae récords y categorías frescos de la web, pero respeta lo que el
    // operador eligió en esta PC.
    return normalizeWrpfRecordsState({
      ...(action as RefreshWrpfRecordsAction).records,
      recordCategoriesOverride: state.recordCategoriesOverride,
    });
  }
  if (action.type === "SET_WRPF_RECORD_CATEGORIES_OVERRIDE") {
    return normalizeWrpfRecordsState({
      ...state,
      recordCategoriesOverride: (action as SetWrpfRecordCategoriesOverrideAction).categories,
    });
  }
  return state;
}
